import type { IconName } from '../components/ui/icons.js';
import type { AssetsSort } from '../state/assets.js';
import { assetKind, type AssetKind } from '../state/project-files.js';
import { fileTypeName } from './asset-types.js';
import type { TreeEntry } from './tree.js';

/** Assets browser search and type filters (pure, unit-tested). */

export const parentOf = (path: string) => (path.includes('/') ? path.slice(0, path.lastIndexOf('/')) : '');

/** Type filters (Unity's "Search by Type", Blender's ID filter). */
export const TYPE_FILTERS: { id: string; label: string; icon: IconName; test: (kind: AssetKind, path: string) => boolean }[] = [
  { id: 'model', label: 'Models', icon: 'model', test: (k) => k === 'model' || k === 'mesh' },
  { id: 'prefab', label: 'Prefabs', icon: 'prefab', test: (k) => k === 'prefab' },
  { id: 'material', label: 'Materials', icon: 'material', test: (k, p) => k === 'material' || /\.(physicsmat\.json|wgsl)$/i.test(p) },
  { id: 'texture', label: 'Textures & sprites', icon: 'image', test: (k) => k === 'texture' || k === 'pixel' },
  { id: 'audio', label: 'Audio', icon: 'audio', test: (k, p) => k === 'audio' || k === 'sound' || /\.mixer\.json$/i.test(p) },
  { id: 'animation', label: 'Animation', icon: 'play', test: (_k, p) => /\.controller\.json$/i.test(p) },
  { id: 'scene', label: 'Scenes', icon: 'scene', test: (k) => k === 'scene' },
  { id: 'script', label: 'Scripts', icon: 'script', test: (k) => k === 'script' },
  { id: 'ui', label: 'UI', icon: 'ui', test: (k) => k === 'ui' || k === 'font' },
  { id: 'data', label: 'Data & other', icon: 'data', test: (k) => k === 'data' || k === 'doc' || k === 'file' || k === 'project' },
];


/** Search text → words plus Unity-style `t:type` and `l:label` filters. */
export function parseAssetQuery(query: string): { words: string[]; types: string[]; labels: string[] } {
  const words: string[] = [];
  const types: string[] = [];
  const labels: string[] = [];
  for (const token of query.trim().toLowerCase().split(/\s+/).filter(Boolean)) {
    if (token.startsWith('t:') && token.length > 2) types.push(token.slice(2));
    else if (token.startsWith('l:') && token.length > 2) labels.push(token.slice(2));
    else words.push(token);
  }
  return { words, types, labels };
}

/** True when a file passes the type filter ids / `t:` names and label filters. */
export function matchesAssetFilters(
  path: string,
  fileLabels: readonly string[],
  filters: { types: readonly string[]; queryTypes: readonly string[]; labels: readonly string[]; words: readonly string[] },
): boolean {
  const name = path.slice(path.lastIndexOf('/') + 1);
  const kind = assetKind({ name, kind: 'file' });
  const lower = path.toLowerCase();
  if (filters.types.length && !TYPE_FILTERS.some((f) => filters.types.includes(f.id) && f.test(kind, path))) return false;
  for (const t of filters.queryTypes) {
    const typeName = (fileTypeName(path) ?? '').toLowerCase().replace(/\s+/g, '');
    const filter = TYPE_FILTERS.find((f) => f.id === t || f.label.toLowerCase().startsWith(t));
    if (!(kind === t || typeName.startsWith(t) || (filter && filter.test(kind, path)))) return false;
  }
  const lowerLabels = fileLabels.map((l) => l.toLowerCase());
  if (!filters.labels.every((l) => lowerLabels.some((x) => x.startsWith(l)))) return false;
  return filters.words.every((w) => lower.includes(w) || lowerLabels.some((l) => l.includes(w)));
}

/**
 * Sorts browser entries: folders first (by name), then files by `sort`
 * (name, type, modification time or size; ties by name), optionally reversed.
 */
export function sortAssetEntries<T extends Pick<TreeEntry, 'path' | 'name' | 'kind' | 'size' | 'modifiedAt'>>(
  entries: readonly T[],
  sort: AssetsSort,
  descending = false,
): T[] {
  const byName = (a: T, b: T) => a.name.localeCompare(b.name, undefined, { numeric: true });
  const typeOf = (e: T) => (fileTypeName(e.path) ?? e.name.slice(e.name.lastIndexOf('.') + 1)).toLowerCase();
  const key = (a: T, b: T): number => {
    if (sort === 'type') return typeOf(a).localeCompare(typeOf(b)) || byName(a, b);
    if (sort === 'modified') return Date.parse(a.modifiedAt) - Date.parse(b.modifiedAt) || byName(a, b);
    if (sort === 'size') return a.size - b.size || byName(a, b);
    return byName(a, b);
  };
  const dirs = entries.filter((e) => e.kind === 'directory').sort(byName);
  const files = entries.filter((e) => e.kind !== 'directory').sort((a, b) => (descending ? -key(a, b) : key(a, b)));
  return [...dirs, ...files];
}

/** "1.2 MB", "340 KB", "12 B". */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(bytes < 10 * 1024 ? 1 : 0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
